import React, { useState } from 'react';
import { Theme, Typography, createStyles, makeStyles } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCircleDollar,
    faPaw,
    faFileSignature,
    faImagesUser,
    faHeartPulse,
    faCalendarDays,
    faCreditCard,
    faMemoCircleCheck,
    faMedal,
    faStar
} from '@fortawesome/pro-light-svg-icons';
import { faArrowLeft, faFileAlt, faGauge, faPhoneFlip } from '@fortawesome/pro-regular-svg-icons';
import Client from '@spike/client-model';
import { formatPhoneNumber } from '@spike/phone-utils';
import { useMarketplace, usePermissions, useRoles } from 'hooks';
import Button from 'components/UI/V3/Button/Button';
import { ActionsPopup } from 'components/UI/ActionsPopup/ActionsPopup';
import { UnderlinedButton } from 'components/UI/UnderlinedButton';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import {
    AddPetKey,
    AgreementsKey,
    ClientKey,
    OverviewKey,
    HistoryKey,
    PetsKey,
    PrefixPetKey,
    WalletKey,
    RewardsKey,
    IntakeFormKey,
    FormsKey,
    DocumentsKey,
    TransactionsKey,
    MembershipsKey
} from 'pages/Client';
import { RootState } from 'store';
import Pet from '@spike/pet-model';
import { useSelector } from 'react-redux';
import MobileButtonActions from './MobileButtonActions';
import Tag from 'components/UI/V2/Tag/Tag';

type ClientEditHeaderProps = {
    client: Client;
    onBook: () => void;
    onNewSale?: () => void;
    showTransactions?: boolean;
    onClose: () => void;
    onSelect: (key: string) => void;
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            display: 'flex',
            flexDirection: 'column',
            borderBottom: 'solid 1px #D4D4D4',
            paddingBottom: 24,
            marginBottom: 24,
            [theme.breakpoints.up('md')]: {
                display: 'none'
            }
        },
        topRow: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start'
        },
        buttonRow: {
            'display': 'flex',
            'gap': 8,
            'marginTop': 16,

            '& button': {
                flex: 1
            }
        },
        clientInfo: {
            'gap': 10,
            'maxWidth': '50%',
            'display': 'flex',
            'alignItems': 'flex-start',

            '& h3': {
                fontSize: 20,
                lineHeight: 1.2,
                fontWeight: 600,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis'
            },
            '& p': {
                'fontSize': 15,
                'lineHeight': 1,

                '& svg': {
                    fontSize: 18,
                    marginRight: 6,
                    verticalAlign: 'middle'
                }
            }
        },
        clientInfoMeta: {
            gap: 8,
            display: 'flex',
            flexDirection: 'column'
        },
        back: {
            all: 'unset',
            fontSize: 20,
            lineHeight: 1,
            padding: '2px 0px'
        },
        clientActions: {
            display: 'flex',
            flexWrap: 'wrap'
        },
        bookButton: {
            'height': 34,
            'padding': '0px 24px',

            '& span': {
                fontSize: 14
            }
        },
        actionsDropdownButton: {
            all: 'unset',
            width: 35,
            height: 35,
            borderRadius: '50%',
            textAlign: 'center',
            backgroundColor: '#F1F1F1'
        }
    })
);

export const ClientEditHeader: React.FunctionComponent<ClientEditHeaderProps> = props => {
    const classes = useStyles();

    const { isOwner, isManager } = useRoles();
    const permissions = usePermissions();
    const isOwnerOrManager = isOwner || isManager;
    const marketplace = useMarketplace();
    const canAccessClientWallet = permissions.checkout.takePayments;
    const canAccessClientTransactions = props.showTransactions ?? (isOwnerOrManager && Boolean(props.onNewSale));

    const pets = useSelector<RootState, Array<Pet>>(state => state.pets.list);

    const [showActions, setShowActions] = useState(false);

    const clientHasIntakeFormResponses = React.useMemo(() => {
        return (
            props.client.customQuestionResponses.length > 0 || pets.some(pet => pet.customQuestionResponses.length > 0)
        );
    }, [props.client, pets]);

    return (
        <>
            <div className={classes.root}>
                <div className={classes.topRow}>
                    <div className={classes.clientInfo}>
                        <button
                            type="button"
                            aria-label="Go back"
                            className={classes.back}
                            onClick={props.onClose}
                        >
                            <FontAwesomeIcon icon={faArrowLeft} />
                        </button>

                        <div className={classes.clientInfoMeta}>
                            <Typography variant="h3">
                                {props.client.firstName} {props.client.lastName}
                            </Typography>

                            <Typography>
                                <FontAwesomeIcon icon={faPhoneFlip} />
                                {formatPhoneNumber(props.client.phone, String(marketplace.basics.address.country?.id))}
                            </Typography>

                            {props.client.blocked && (
                                <div>
                                    <Tag variant="danger">Blocked</Tag>
                                </div>
                            )}
                        </div>
                    </div>

                    <MobileButtonActions onClikedActions={() => setShowActions(true)} />
                </div>

                {!props.client.blocked && (
                    <div className={classes.buttonRow}>
                        {props.client.pets.length > 0 && (
                            <Button
                                variant="green"
                                label="Book"
                                onClick={props.onBook}
                            />
                        )}
                        {props.onNewSale && (
                            <Button
                                color="black"
                                variant="outlined"
                                label="New Sale"
                                onClick={props.onNewSale}
                            />
                        )}
                    </div>
                )}
            </div>

            {showActions && (
                <ActionsPopup
                    actions={[
                        {
                            label: 'Overview',
                            icon: faGauge,
                            onClick: () => props.onSelect(OverviewKey)
                        },
                        {
                            label: 'Client Information',
                            icon: faImagesUser,
                            onClick: () => props.onSelect(ClientKey)
                        },
                        {
                            icon: faPaw,
                            label: 'Pets',
                            items: pets.map(pet => ({
                                label: (
                                    <>
                                        {pet.name}
                                        {pet.deceased ? ' 🌈' : ''}
                                        {pet.hasMedicalConditions && (
                                            <FontAwesomeIcon
                                                size="lg"
                                                color="#EF4F57"
                                                icon={faHeartPulse}
                                                style={{ marginLeft: 4 }}
                                            />
                                        )}
                                    </>
                                ),
                                onClick: () => props.onSelect(`${PrefixPetKey}${pet.id}`)
                            })),
                            rightElement: (
                                <UnderlinedButton
                                    color="green"
                                    label="Add Pet"
                                    leftIcon={<FontAwesomeIcon icon={faPlus} />}
                                    onClick={() => props.onSelect(AddPetKey)}
                                />
                            ),
                            onClick: () => props.onSelect(PetsKey)
                        },
                        ...(clientHasIntakeFormResponses
                            ? [
                                  {
                                      label: 'Intake Form',
                                      icon: faMemoCircleCheck,
                                      onClick: () => props.onSelect(IntakeFormKey)
                                  }
                              ]
                            : []),
                        {
                            label: 'Forms',
                            icon: faFileAlt,
                            onClick: () => props.onSelect(FormsKey)
                        },
                        {
                            label: 'Documents',
                            icon: faFileAlt,
                            onClick: () => props.onSelect(DocumentsKey)
                        },
                        ...(canAccessClientWallet
                            ? [
                                  {
                                      label: 'Wallet',
                                      icon: faCreditCard,
                                      onClick: () => props.onSelect(WalletKey)
                                  }
                              ]
                            : []),
                        {
                            label: 'Rewards',
                            icon: faMedal,
                            onClick: () => props.onSelect(RewardsKey)
                        },
                        {
                            label: 'Memberships',
                            icon: faStar,
                            onClick: () => props.onSelect(MembershipsKey)
                        },
                        {
                            label: 'History',
                            icon: faCalendarDays,
                            onClick: () => props.onSelect(HistoryKey)
                        },
                        ...(canAccessClientTransactions
                            ? [
                                  {
                                      label: 'Transactions',
                                      icon: faCircleDollar,
                                      onClick: () => props.onSelect(TransactionsKey)
                                  }
                              ]
                            : []),
                        {
                            label: 'Agreements',
                            icon: faFileSignature,
                            onClick: () => props.onSelect(AgreementsKey)
                        }
                    ]}
                    onClose={() => setShowActions(false)}
                />
            )}
        </>
    );
};
